import React, { useState } from "react";

//Colores por estado de la habitación. Se exporta para que la leyenda de la pantalla y el mapa no se
//puedan desincronizar: si aquí cambia un color, la leyenda cambia sola.
export const COLORES_ESTADO = {
  libre: { fondo: "#22c55e1a", borde: "#22c55e99", texto: "#15803d", etiqueta: "Libre" },
  ocupada: { fondo: "#ef44441f", borde: "#ef4444aa", texto: "#b91c1c", etiqueta: "Ocupada" },
  saleHoy: { fondo: "#f59e0b26", borde: "#f59e0baa", texto: "#b45309", etiqueta: "Sale hoy" },
  // Reservada por una venta programada que todavía no se genera. Morado para que no se confunda con
  // ninguno de los tres estados de una venta real, y borde punteado —igual que "sin tarifa"— porque
  // en los dos casos falta algo para que el cuarto esté realmente comprometido.
  programada: { fondo: "#8b5cf61f", borde: "#8b5cf6aa", texto: "#6d28d9", etiqueta: "Programada" },
  sinTarifa: { fondo: "#9ca3af1f", borde: "#9ca3af99", texto: "#4b5563", etiqueta: "Sin tarifa" },
};

// Los estados que se dibujan con borde punteado: nada los ocupa todavía de forma firme.
const ESTADOS_PUNTEADOS = ["sinTarifa", "programada"];

export const COLOR_SOBREVENTA = "#7f1d1d";
export const COLOR_SELECCION = "#00BFD6";

//Cajita de una habitación en el lienzo. Es el mismo componente en el visor y en el editor: lo único
//que cambia es `data.editable`, que habilita el tirador de redimensionado.
//
//El resize se hace a mano con listeners sobre el DOM porque react-flow-renderer 10.x no trae
//NodeResizer (mismo problema y misma solución que RackCanvas.js del mapa de almacén).
const HabitacionCanvas = ({ data, selected }) => {
  const [tamanoPreview, setTamanoPreview] = useState(null);

  const ancho = tamanoPreview?.width ?? data.anchoActual ?? 90;
  const alto = tamanoPreview?.height ?? data.altoActual ?? 70;

  const estado = data.estadoOcupacion || "libre";
  const colores = COLORES_ESTADO[estado] || COLORES_ESTADO.libre;
  const estancia = data.estancia || null;

  const iniciarResize = (event) => {
    event.stopPropagation();
    event.preventDefault();
    const inicioX = event.clientX;
    const inicioY = event.clientY;
    const anchoInicial = data.anchoActual ?? 90;
    const altoInicial = data.altoActual ?? 70;

    const mover = (moveEvent) => {
      setTamanoPreview({
        width: Math.max(50, anchoInicial + (moveEvent.clientX - inicioX)),
        height: Math.max(40, altoInicial + (moveEvent.clientY - inicioY)),
      });
    };
    const soltar = (upEvent) => {
      const nuevoAncho = Math.max(50, anchoInicial + (upEvent.clientX - inicioX));
      const nuevoAlto = Math.max(40, altoInicial + (upEvent.clientY - inicioY));
      document.removeEventListener("mousemove", mover);
      document.removeEventListener("mouseup", soltar);
      setTamanoPreview(null);
      data.onResize && data.onResize({ width: nuevoAncho, height: nuevoAlto });
    };
    document.addEventListener("mousemove", mover);
    document.addEventListener("mouseup", soltar);
  };

  const bordeColor = data.sobreventa
    ? COLOR_SOBREVENTA
    : selected
    ? COLOR_SELECCION
    : colores.borde;
  const bordeAncho = data.sobreventa ? 3 : 2;

  const titulo = [
    `Habitación ${data.numero}${data.nombre ? " - " + data.nombre : ""}`,
    data.insumoDescripcion ? `Tarifa: ${data.insumoDescripcion}` : "Sin insumo de tarifa asignado",
    estancia ? `${estancia.clienteNombre || "Cliente"} (${colores.etiqueta})` : colores.etiqueta,
    data.sobreventa ? "SOBREVENTA: hay más de una estancia viva esta noche" : null,
  ]
    .filter(Boolean)
    .join("\n");

  return (
    <div
      style={{
        width: ancho,
        height: alto,
        background: colores.fondo,
        border: `${bordeAncho}px ${ESTADOS_PUNTEADOS.includes(estado) ? "dashed" : "solid"} ${bordeColor}`,
        borderRadius: 6,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        boxSizing: "border-box",
        overflow: "hidden",
        padding: 2,
        boxShadow: data.sobreventa ? `0 0 0 4px ${COLOR_SOBREVENTA}22` : undefined,
      }}
      title={titulo}
    >
      {data.sobreventa && (
        <div style={{ position: "absolute", top: 1, right: 3, fontSize: 11, lineHeight: 1 }}>⚠</div>
      )}

      {data.entraHoy && (
        <div
          style={{
            position: "absolute",
            top: 1,
            left: 3,
            fontSize: 8,
            fontWeight: 700,
            color: "#1d4ed8",
            lineHeight: 1.2,
          }}
        >
          ENTRA HOY
        </div>
      )}

      <div style={{ fontSize: 15, fontWeight: 700, color: colores.texto, lineHeight: 1.1 }}>
        {data.numero}
      </div>

      {estancia ? (
        <div
          style={{
            fontSize: 9,
            color: colores.texto,
            maxWidth: "100%",
            textAlign: "center",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            padding: "0 2px",
          }}
        >
          {estancia.clienteNombre || "Cliente"}
        </div>
      ) : (
        data.nombre && (
          <div
            style={{
              fontSize: 9,
              color: colores.texto,
              maxWidth: "100%",
              textAlign: "center",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              padding: "0 2px",
            }}
          >
            {data.nombre}
          </div>
        )
      )}

      {/* Punto de saldo: una estancia con saldo pendiente es lo primero que recepción necesita ver
          antes de dejar salir al huésped. */}
      {estancia && estancia.saldo > 0 && (
        <div
          style={{
            position: "absolute",
            bottom: 3,
            right: 4,
            width: 7,
            height: 7,
            borderRadius: "50%",
            background: "#dc2626",
          }}
          title="Tiene saldo pendiente"
        />
      )}

      {data.editable && selected && (
        <div
          className="nodrag"
          onMouseDown={iniciarResize}
          style={{
            position: "absolute",
            right: 0,
            bottom: 0,
            width: 10,
            height: 10,
            cursor: "nwse-resize",
            background: COLOR_SELECCION,
          }}
        />
      )}
    </div>
  );
};

export default HabitacionCanvas;
